import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * Production lead-time ("Délai de Fabrication") picker for made-to-order bridal products.
 *
 * The server decides whether the picker should be shown: snippets/delai-fabrication-picker.liquid
 * renders the host element on every load — hidden and EMPTY when there is nothing to offer — so
 * there is always a morph target here, even for a variant that is in stock.
 *
 * On every variant change the theme re-renders the section and hands us the new document, so we
 * simply adopt the server's markup. That keeps the in-stock decision in exactly one place.
 *
 * The server DOES check the free 8-week tier (see the snippet for why), which makes morphing
 * hazardous: the incoming markup always carries `checked` on the default, so adopting it wholesale
 * would silently reset a shopper who had chosen — and is about to pay for — a faster tier. We
 * therefore snapshot the selection and the disclosure state before the morph and restore them
 * after. A selection is only ever lost when the picker legitimately disappears (the shopper
 * switched to an in-stock size) or when the chosen tier is absent from the new markup.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement[]} [options] - The lead-time radio inputs.
 * @property {HTMLButtonElement} [toggle] - The upgrade-options disclosure button.
 * @extends {Component<Refs>}
 */
class DelaiFabricationPicker extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#syncDisabledState();
    this.#ensureSelection();
    this.addEventListener('change', this.#onOptionChange);
    this.#closestSection()?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onOptionChange);
    this.#closestSection()?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncDisabledState();
  }

  /**
   * The nearest section or dialog. Scoping the listener here — rather than on `document` —
   * keeps quick-add drawers and related-product carousels from cross-talking. The `dialog`
   * half of the selector is what makes quick-add work.
   *
   * @returns {Element | null}
   */
  #closestSection() {
    return this.closest('.shopify-section, dialog');
  }

  /**
   * Adopts the server-rendered picker for the newly selected variant.
   *
   * @param {Event} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;

    // A combined listing can swap the product underneath us; adopt the new id, otherwise
    // ignore updates that belong to a different product's picker.
    if (detail.data?.newProduct) {
      this.dataset.productId = detail.data.newProduct.id;
    } else if (detail.data?.productId && detail.data.productId !== this.dataset.productId) {
      return;
    }

    const incoming = detail.data?.html?.querySelector('delai-fabrication-picker');
    if (!incoming) return;

    // Snapshot BEFORE the morph — both are shopper state the server knows nothing about.
    const previousValue = this.#checkedValue();
    const wasExpanded = this.refs.toggle?.getAttribute('aria-expanded') === 'true';

    // Mirror the host's own attributes (`hidden` above all) — the children alone do not
    // carry the show/hide decision.
    if (incoming.hasAttribute('hidden')) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }

    // Omit the options argument: morph.js REPLACES its options object rather than merging
    // (see `options = MORPH_OPTIONS` at morph.js:93), so passing `{ childrenOnly: true }`
    // would discard the theme's `reject` hook — which strips whitespace-only text nodes and
    // the Section Rendering API comment node that would otherwise accumulate inside the
    // picker on every Theme Editor re-render. MORPH_OPTIONS already sets childrenOnly.
    morph(this, incoming);

    // Restore only when the picker survived. If it is now hidden the whole control is off
    // the table, and re-checking anything would just be a phantom surcharge waiting for
    // #syncDisabledState to catch it.
    if (!this.hasAttribute('hidden')) {
      this.#restoreSelection(previousValue);
      this.#restoreExpanded(wasExpanded);
    }

    // The default options DO restore the `onAfterUpdate` -> updatedCallback() hook, but we
    // re-sync explicitly anyway: this is a money guard, and it should not depend on a hook
    // firing for this particular element.
    this.#syncDisabledState();
  };

  /**
   * The value of the currently checked lead-time radio, if any.
   *
   * @returns {string | null}
   */
  #checkedValue() {
    const options = this.refs.options ?? [];
    return options.find((option) => option.checked)?.value ?? null;
  }

  /**
   * Guarantees this picker owns a checked radio.
   *
   * The radios are named `properties[Délai de Fabrication]`, so ANY other control on the
   * page using that same name joins the same radio group and can take the checked flag
   * away after our markup has parsed — the legacy ShopPad / Infinite Options widget does
   * exactly this while it is still installed.
   *
   * The stylesheet has a matching fallback that shows the free row when nothing is
   * checked, but CSS can only fix the PICTURE. Left alone, the summary would display
   * "Fabrication en 8 semaines" while the form submitted no lead-time property at all:
   * a made-to-order garment reaching the atelier with no fabrication instruction. This
   * re-checks the free tier so the displayed value and the submitted value agree.
   *
   * Only ever selects the 0 EUR tier, identified by price exactly as the server
   * identifies it — never a paid one, which would charge for something nobody picked.
   * A shopper's real selection is left untouched.
   */
  #ensureSelection() {
    const options = this.refs.options ?? [];

    if (options.length === 0) return;
    if (options.some((option) => option.checked)) return;

    const fallback = options.find((option) => option.dataset.surchargePrice === '0');

    if (fallback) fallback.checked = true;
  }

  /**
   * Re-applies the shopper's pre-morph choice over the server's default.
   *
   * The fallback matters: a tier can genuinely vanish between renders (the merchandiser
   * repoints the collection, or the add-on is pulled). Leaving nothing checked there would
   * submit the garment with no lead-time property at all, so we fall back to the free
   * default tier — never to a paid one, which would charge for something nobody picked.
   *
   * @param {string | null} previousValue - The value checked before the morph.
   */
  #restoreSelection(previousValue) {
    const options = this.refs.options ?? [];
    if (options.length === 0) return;

    // Nothing was checked before, so the server's own default is the best answer.
    if (previousValue === null) return;

    // Already correct: the server happened to re-check what the shopper had chosen.
    if (this.#checkedValue() === previousValue) return;

    const restored = options.find((option) => option.value === previousValue);

    if (restored) {
      restored.checked = true;
      return;
    }

    // The chosen tier no longer exists. Fall back to the free one — same rule, same one
    // place: #ensureSelection owns "if nothing is checked, check the 0 EUR tier".
    this.#ensureSelection();
  }

  /**
   * Reopens the disclosure if it was open before the morph, so a variant change does not
   * collapse the upgrade ladder out from under a shopper who is mid-comparison.
   *
   * @param {boolean} wasExpanded - Whether the disclosure was open before the morph.
   */
  #restoreExpanded(wasExpanded) {
    if (!wasExpanded) return;
    this.refs.toggle?.setAttribute('aria-expanded', 'true');
  }

  /**
   * Toggles the paid upgrade options. Bound declaratively via `on:click` in the snippet.
   *
   * `aria-expanded` is the ONLY state written: the CSS keys the height animation, the
   * chevron rotation and the toggle's own label off that attribute, so the announced state
   * and the visible state cannot drift. The radios themselves are never disabled here —
   * collapsing is presentation only, and a selection made while expanded must still submit.
   */
  toggleOptions = () => {
    const toggle = this.refs.toggle;
    if (!toggle) return;

    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
  };

  /**
   * Closes the ladder as soon as a tier is chosen.
   *
   * Picking a lead time is a single decision, so the list has no reason to stay open once
   * it is made — the collapsed row then shows the new choice, which is the whole point of
   * the summary. Focus returns to the toggle because the element the shopper was on is
   * about to be visually collapsed, and leaving focus inside a collapsed region strands
   * keyboard and screen-reader users.
   *
   * Bound on the container rather than per radio: `change` bubbles, and the picker is
   * re-rendered wholesale on every variant update, so a delegated listener survives a
   * morph that per-input listeners would not.
   */
  #onOptionChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

    const toggle = this.refs.toggle;
    if (!toggle || toggle.getAttribute('aria-expanded') !== 'true') return;

    toggle.setAttribute('aria-expanded', 'false');
    toggle.focus();
  };

  /**
   * Keeps the radios out of the submitted form whenever the picker is not on offer.
   *
   * This is the safety net that matters: a hidden-but-checked radio is still serialised by
   * FormData, which would attach a phantom surcharge to an in-stock purchase. Disabled
   * inputs are excluded from FormData, so we disable in addition to hiding.
   */
  #syncDisabledState() {
    const hidden = this.hasAttribute('hidden');
    const options = this.refs.options ?? [];

    for (const option of options) {
      option.disabled = hidden;
    }
  }
}

if (!customElements.get('delai-fabrication-picker')) {
  customElements.define('delai-fabrication-picker', DelaiFabricationPicker);
}
